import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import React, { useState, useEffect } from 'react';
import { message } from 'antd';
import {
  AvatarDropdown,
  AvatarName,
  Google2FAModal,
} from '@/components';
import { currentUser as queryCurrentUser, outLogin } from '@/services/ant-design-pro/api';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';
import { getApiBaseUrl } from '@/utils/apiConfig';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
  showGoogle2FAModal?: boolean;
}> {
  const fetchUserInfo = async () => {
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      return msg;
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };
  // 如果不是登录页面，执行
  const { location } = history;
  if (
    ![loginPath, '/user/register', '/user/register-result'].includes(
      location.pathname,
    )
  ) {
    const currentUser = await fetchUserInfo();

    // 检查是否需要显示谷歌验证码绑定弹窗
    const showGoogle2FAModal = currentUser && currentUser.google2faEnabled === false;

    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
      showGoogle2FAModal,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  // 谷歌验证码弹窗状态管理
  const [google2FAModalVisible, setGoogle2FAModalVisible] = useState(false);

  // 处理谷歌验证码绑定成功
  const handleGoogle2FASuccess = async () => {
    setGoogle2FAModalVisible(false);

    // 显示退出登录提示
    message.info('谷歌验证码绑定成功，正在退出登录...');

    try {
      // 调用退出登录接口
      await outLogin();

      // 清除本地状态
      setInitialState((prev) => ({
        ...prev,
        currentUser: undefined,
        showGoogle2FAModal: false,
      }));

      // 跳转到登录页面
      history.push('/user/login');

      message.success('已退出登录，请重新登录');
    } catch (error) {
      console.error('退出登录失败:', error);
      // 即使退出失败，也跳转到登录页面
      history.push('/user/login');
    }
  };

  // 处理谷歌验证码弹窗取消
  const handleGoogle2FACancel = () => {
    setGoogle2FAModalVisible(false);
    setInitialState((prev) => ({
      ...prev,
      showGoogle2FAModal: false,
    }));
  };

  // 监听showGoogle2FAModal状态变化
  useEffect(() => {
    if (initialState?.showGoogle2FAModal) {
      setGoogle2FAModalVisible(true);
    }
  }, [initialState?.showGoogle2FAModal]);

  return {
    avatarProps: {
      // src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    // waterMarkProps: {
    //   content: initialState?.currentUser?.name,
    // },
    // footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {/* 谷歌验证码绑定弹窗 */}
          <Google2FAModal
            visible={google2FAModalVisible}
            onCancel={handleGoogle2FACancel}
            onSuccess={handleGoogle2FASuccess}
          />
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL: getApiBaseUrl(),
  ...errorConfig,
  timeout: 10000,
};
